import { LOADING_TRUCK_TYPE, LOADING_TRUCK_TYPES, RETIRED_TRUCK_TYPE } from "../../convex/constants/types.lib";
import type { LoadingTruckType } from "../../convex/schemas/validators/enums.lib";
import { isMemberOf, isUnknownRecord } from "../validation/narrow";

export const DEFAULT_CHECKOUT_TRUCK_CAPACITY = 500;

export function isLoadingTruckType(truckType: string): truckType is LoadingTruckType {
  return isMemberOf(LOADING_TRUCK_TYPES, truckType);
}

export interface ResolvedTruckCapacity {
  capacity: number;
  truckType: string;
}

export function resolvePrimaryTruckCapacity(
  capacities: unknown,
  defaults?: unknown,
): ResolvedTruckCapacity | null {
  const resolved = resolveTruckCapacities(capacities, defaults);
  return resolved.reduce<ResolvedTruckCapacity | null>(
    (largest, current) =>
      !largest || current.capacity > largest.capacity ? current : largest,
    null,
  );
}

// WHY: founder ruling 2026-10-28 (TKT-005263): a box truck or cargo van is the seller's statement that a small load fits, so a product-type default never turns one on.
const FULL_TRAILERS: ReadonlySet<string> = new Set([LOADING_TRUCK_TYPE.DRY_VAN, LOADING_TRUCK_TYPE.FLATBED]);

export function resolveCheckoutTruckOptions(
  capacities: unknown,
  defaults?: unknown,
): ResolvedTruckCapacity[] {
  if (isUnknownRecord(capacities)) return readStandardCapacities(capacities);
  if (!isUnknownRecord(defaults))
    return [{ truckType: LOADING_TRUCK_TYPE.DRY_VAN, capacity: DEFAULT_CHECKOUT_TRUCK_CAPACITY }];
  return readDefaultCapacities(defaults).filter((option) => FULL_TRAILERS.has(option.truckType));
}

export function resolveSelectedTruckCapacity(
  capacities: unknown,
  defaults?: unknown,
  truckType?: string,
  quantity?: number,
): ResolvedTruckCapacity | null {
  const options = resolveCheckoutTruckOptions(capacities, defaults);
  return truckType
    ? (options.find((option) => option.truckType === truckType) ?? null)
    : (options.find(
        (option) => quantity === undefined || option.capacity >= quantity,
      ) ?? null);
}

export function resolveTruckCapacities(
  capacities: unknown,
  defaults?: unknown,
): ResolvedTruckCapacity[] {
  return isUnknownRecord(capacities)
    ? readConfiguredCapacities(capacities)
    : readDefaultCapacities(defaults);
}

function readConfiguredCapacities(
  input: Record<string, unknown>,
): ResolvedTruckCapacity[] {
  const standard = readStandardCapacities(input);
  const custom = Array.isArray(input.custom)
    ? input.custom.flatMap((config) => {
        if (!isUnknownRecord(config) || config.enabled !== true) return [];
        const truckType =
          typeof config.truckTypeId === "string" && config.truckTypeId
            ? config.truckTypeId
            : "custom";
        return toCapacity(config.units, truckType);
      })
    : [];
  return [...standard, ...custom];
}

function readStandardCapacities(
  input: Record<string, unknown>,
): ResolvedTruckCapacity[] {
  return LOADING_TRUCK_TYPES.flatMap((truckType) => {
    const config = enabledConfig(input[truckType]) ?? enabledRetiredConfig(input, truckType);
    return config ? toCapacity(config.units, truckType) : [];
  });
}

const RETIRED_TRUCK_TYPE_FOR: Partial<Record<LoadingTruckType, string>> = {
  [LOADING_TRUCK_TYPE.CARGO_VAN]: RETIRED_TRUCK_TYPE.PICKUP_TRUCK,
  [LOADING_TRUCK_TYPE.DRY_VAN]: RETIRED_TRUCK_TYPE.DROP_TRAILER,
};

function enabledConfig(config: unknown): Record<string, unknown> | undefined {
  return isUnknownRecord(config) && config.enabled === true ? config : undefined;
}

function enabledRetiredConfig(
  input: Record<string, unknown>,
  truckType: LoadingTruckType,
): Record<string, unknown> | undefined {
  const retired = RETIRED_TRUCK_TYPE_FOR[truckType];
  if (!retired || (truckType === LOADING_TRUCK_TYPE.CARGO_VAN && isUnknownRecord(input[LOADING_TRUCK_TYPE.CARGO_VAN]))) return undefined;
  const config = enabledConfig(input[retired]);
  const own = input[truckType];
  if (!config || config.units !== undefined || !isUnknownRecord(own)) return config;
  return { ...config, units: own.units };
}

function readDefaultCapacities(input: unknown): ResolvedTruckCapacity[] {
  if (!isUnknownRecord(input)) return [];
  return LOADING_TRUCK_TYPES.flatMap((truckType) =>
    toCapacity(input[truckType] ?? (truckType === LOADING_TRUCK_TYPE.CARGO_VAN ? input[RETIRED_TRUCK_TYPE.PICKUP_TRUCK] : undefined), truckType),
  );
}

function toCapacity(
  units: unknown,
  truckType: string,
): ResolvedTruckCapacity[] {
  if (typeof units !== "number" || !Number.isSafeInteger(units) || units < 1)
    return [];
  return [{ capacity: units, truckType }];
}
